/**
 * Shared date/time formatters. Every page used to ship its own copy of
 * fmtDate/fmtDateTime — replace those with imports from here so the whole
 * app shows timestamps the same way.
 */

/**
 * Parse a date input into a Date, correctly handling the Indian DD/MM/YYYY
 * format the sheets emit (e.g. GRN createdAt "04/06/2026 12:37:30").
 *
 * `new Date("04/06/2026")` mis-reads that as US MM/DD = April 6, which made
 * list dates show the day and month SWAPPED ("6 Apr" instead of "4 Jun").
 * ISO strings ("2026-06-04", "2026-06-04T..Z") and Date objects pass straight
 * through to the native parser, which handles them unambiguously.
 */
export function parseDate(input?: string | Date | null): Date | null {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const s = String(input).trim();
  if (!s) return null;
  // DD/MM/YYYY or DD-MM-YYYY, optionally followed by HH:MM[:SS]. Day-first
  // (Indian) — the app's sheets format dates this way. ISO (YYYY-...) does NOT
  // match this pattern, so it falls through to native parsing below.
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) {
    const day = Number(m[1]); const mon = Number(m[2]); const year = Number(m[3]);
    if (mon >= 1 && mon <= 12 && day >= 1 && day <= 31) {
      const d = new Date(year, mon - 1, day, Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0));
      if (!isNaN(d.getTime())) return d;
    }
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/** Locale date only — e.g. "5/5/2026". Returns em-dash on bad input. */
export function fmtDate(iso?: string | Date | null): string {
  if (!iso) return '—';
  const d = parseDate(iso);
  if (!d) {
    // Last-resort: surface a bare ISO date string if native parsing failed.
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return String(iso || '—');
  }
  return d.toLocaleDateString();
}

/** Locale date + time — e.g. "5/5/2026, 1:53:54 PM". */
export function fmtDateTime(iso?: string | Date | null): string {
  if (!iso) return '—';
  const d = parseDate(iso);
  return d ? d.toLocaleString() : String(iso || '—');
}

/**
 * Compact relative-time hint, e.g. "2h ago", "3d ago", "just now".
 * Falls back to date when older than 30 days.
 */
export function fmtRelative(iso?: string | Date | null): string {
  if (!iso) return '—';
  const d = parseDate(iso);
  if (!d) return '—';
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 30 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return fmtDate(d);
}

/** Compact date+time used inline next to a doc number. e.g. "5 May, 1:53 PM". */
export function fmtCompact(iso?: string | Date | null): string {
  if (!iso) return '';
  const d = parseDate(iso);
  if (!d) return '';
  const day = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time}`;
}
